<template>
  <div>
    <v-alert
      v-if="progressMsg"
      type="info"
      class="mb-n12 mx-4 mt-4"
      text
      dismissible
    >
      {{ progressMsg }}
    </v-alert>
    <v-container
      fluid
      :class="{
        'mx-12': !mobile,
        'my-6': !mobile
      }"
    >
      <div
        :class="{
          'd-flex': true,
          'flex-row': !mobile,
          'flex-column': mobile,
          'mb-4': !mobile
        }"
      >
        <div class="card-label d-flex flex-row mb-4">
          <v-icon>mdi-cash-multiple</v-icon>
          <p class="mb-0 ml-4 subtitle-1 font-weight-bold">Split View</p>
        </div>
        <div v-if="!mobile" class="flex-grow-1"></div>
        <div
          :class="{
            'd-flex': true,
            'flex-row': true,
            'justify-space-between': mobile,
            'mb-4': mobile
          }"
        ></div>
        <dots-menu :menu-items="menuItems"></dots-menu>
      </div>
      <v-row class="mb-0">
        <v-col class="py-0" cols="12" sm="12" md="4">
          <v-text-field
            v-model="searchString"
            solo
            flat
            append-icon="mdi-magnify"
            label="Search Tenant/Unit"
            hide-details
            class="mb-6"
            :dense="mobile"
            @keydown.enter="searchAction"
          >
          </v-text-field>
        </v-col>
        <v-col
          cols="8"
          sm="8"
          md="3"
          :class="{
            'py-0': true,
            'd-flex': mobile,
            'align-center': mobile
          }"
        >
          <v-select
            v-model="selectedCategories"
            solo
            flat
            hide-details
            label="Select Categories"
            :items="categories"
            multiple
            :dense="mobile"
            @change="searchAction"
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @click="selectAllCategoriesAndReload">
                <v-list-item-action>
                  <v-icon
                    :color="
                      selectedCategories.length > 0 ? 'indigo darken-4' : ''
                    "
                    >{{
                      iconSelected(
                        allCategoriesSelected,
                        someCategoriesSelected
                      )
                    }}</v-icon
                  >
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ selectedCategories.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col
          cols="4"
          sm="4"
          md="3"
          :class="{
            'py-0': true,
            'd-flex': mobile,
            'align-center': mobile,
            'justify-center': mobile
          }"
        >
          <v-select
            v-model="selectedStatuses"
            solo
            flat
            hide-details
            label="Select Status"
            :items="statusItems"
            :dense="mobile"
            multiple
            @change="searchAction"
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @click="selectAllStatusesAndReload">
                <v-list-item-action>
                  <v-icon
                    :color="
                      selectedStatuses.length > 0 ? 'indigo darken-4' : ''
                    "
                    >{{ iconStatuses }}</v-icon
                  >
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ selectedStatuses.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col
          cols="8"
          sm="8"
          md="2"
          :class="{
            'py-0': true,
            'd-flex': mobile,
            'align-center': mobile
          }"
        >
          <v-select
            v-model="selectedTags"
            solo
            flat
            hide-details
            label="Select Tags"
            :items="propertyTags"
            multiple
            :dense="mobile"
            @change="searchAction"
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @click="selectAllTags">
                <v-list-item-action>
                  <v-icon
                    :color="selectedTags.length > 0 ? 'indigo darken-4' : ''"
                    >{{
                      iconSelected(allTagsSelected, someTagsSelected)
                    }}</v-icon
                  >
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ selectedTags.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </v-col>
      </v-row>
      <payment-request-table
        :search-string="searchString"
        :selected-statuses="selectedStatuses"
        :selected-categories="selectedCategories"
        :selected-tags="selectedTags"
        :reload-trigger="reloadTrigger"
        @reload="reload"
      ></payment-request-table>
      <v-divider class="my-8"></v-divider>
      <paid-payment-table
        :search-string="searchString"
        :selected-statuses="selectedStatuses"
        :selected-categories="selectedCategories"
        :selected-tags="selectedTags"
        :reload-trigger="reloadTrigger"
        @reload="reload"
      ></paid-payment-table>
      <v-skeleton-loader
        v-show="loading"
        ref="skeleton"
        type="table"
        loading
        class="mx-auto"
      ></v-skeleton-loader>
      <v-skeleton-loader
        v-show="loading"
        ref="skeleton"
        type="table"
        loading
        class="mx-auto"
      ></v-skeleton-loader>
    </v-container>
    <VoidPaidPayment ref="voidPaidPayment"></VoidPaidPayment>
    <CancelPaidPayment ref="cancelPaidPayment"></CancelPaidPayment>
    <GenerateReport ref="generateReport"></GenerateReport>
    <CreateMultiplePayments
      ref="createMultiplePayments"
    ></CreateMultiplePayments>
    <ApprovePaidPayment ref="approvePaidPayment"></ApprovePaidPayment>
    <paid-payment-invoice ref="paidPaymentInvoice"></paid-payment-invoice>
  </div>
</template>

<script>
import VoidPaidPayment from '../../components/payment/VoidPaidPayment.vue'
import CancelPaidPayment from '../../components/payment/CancelPaidPayment.vue'
import GenerateReport from '../../components/payment/GenerateReport.vue'
import CreateMultiplePayments from '../../components/payment/CreateMultiplePayments.vue'
import ApprovePaidPayment from '../../components/payment/ApprovePaidPayment.vue'
import PaidPaymentInvoice from '../../components/payment/PaidPaymentInvoice.vue'
import PaymentRequestTable from '../../components/tables/PaymentRequestTable.vue'
import PaidPaymentTable from '../../components/tables/PaidPaymentTable.vue'
import DotsMenu from '../../components/menu/DotsMenu.vue'

import {
  paymentStatusSelect,
  PaymentStatus,
  PaymentCategories,
  PaidPaymentStatus
} from '../../config/payment'

export default {
  components: {
    CancelPaidPayment,
    GenerateReport,
    CreateMultiplePayments,
    VoidPaidPayment,
    ApprovePaidPayment,
    PaidPaymentInvoice,
    PaymentRequestTable,
    PaidPaymentTable,
    DotsMenu
  },
  data() {
    return {
      searchString: '',
      loading: true,
      limit: 0,
      total: 0,
      item: {},
      propertyTags: [],
      selectedTags: [],
      statusItems: paymentStatusSelect,
      selectedCategories: [],
      categories: [],
      progressMsg: '',
      expandedPayments: [],
      allExpanded: false,
      PaymentStatus,
      PaymentCategories,
      selectedStatuses: [],
      PaidPaymentStatus,
      reloadTrigger: true
    }
  },
  computed: {
    property() {
      return this.$store.getters['properties/getProperty']
    },
    allCategoriesSelected() {
      return (
        this.selectedCategories.length ===
        (this.categories && this.categories.length)
      )
    },
    someCategoriesSelected() {
      return this.selectedCategories.length > 0 && !this.allCategoriesSelected
    },
    allTagsSelected() {
      return (
        this.selectedTags.length ===
        (this.propertyTags && this.propertyTags.length)
      )
    },
    someTagsSelected() {
      return this.selectedTags.length > 0 && !this.allTagsSelected
    },
    iconCategories() {
      if (this.allCategoriesSelected) return 'mdi-close-box'
      if (this.someCategoriesSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    iconStatuses() {
      if (this.allStatusesSelected) return 'mdi-close-box'
      if (this.someStatusesSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    allStatusesSelected() {
      return (
        this.selectedStatuses.length ===
        (this.statusItems && this.statusItems.length)
      )
    },
    someStatusesSelected() {
      return this.selectedStatuses.length > 0 && !this.allStatusesSelected
    },
    user() {
      return this.$store.getters['auth/getUser']
    },
    paymentAccess() {
      return this.$root.$roleAccess('payment').access
    },
    paidpaymentAccess() {
      return this.$root.$roleAccess('paidpayment').access
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    },
    menuItems() {
      return [
        {
          icon: 'mdi-file-upload',
          title: 'Import file',
          action: this.openCreateMultiplePaymentsDialog,
          disabled: !this.paidpaymentAccess.create,
          message: 'Create batch payments by uploading a CSV File'
        },
        {
          icon: 'mdi-file-table',
          title: 'Generate Report',
          action: this.openGenerateReportDialog,
          disabled: !this.paymentAccess.report,
          message: 'Download the transactions as an Excel-compatible CSV file'
        }
      ]
    }
  },
  async mounted() {
    this.loading = true
    this.$socket.client.emit('init', this.user.userId)
    await this.loadCategories()
    await this.reload()
    this.loading = false
  },
  methods: {
    async reload() {
      this.loading = true
      await this.loadCategories()
      this.selectedCategories = this.categories.slice().map((t) => t.value)
      this.selectedStatuses = this.statusItems.slice().map((t) => t.value)
      this.propertyTags = this.property.tags

      await this.searchAction()

      if (this.allExpanded) {
        this.expandedPayments = this.payments
      } else {
        this.expandedPayments = []
      }
      this.loading = false
    },
    async loadCategories() {
      let categories = await this.$store.getters['properties/getProperty']
        .payment_categories_details[0].categories
      this.categories = [...categories] //shallow copy so that the 'push' line below doesn't modify the categories in the property store
      this.categories.push({ text: 'Deleted', value: 'deleted' })
    },
    async searchAction() {
      this.reloadTrigger = !this.reloadTrigger
    },
    openGenerateReportDialog() {
      this.$refs.generateReport.open()
    },
    selectAllCategories() {
      if (this.allCategoriesSelected) {
        this.selectedCategories = []
      } else {
        this.selectedCategories = this.categories.slice().map((t) => t.value)
      }
    },
    selectAllStatuses() {
      if (this.allStatusesSelected) {
        this.selectedStatuses = []
      } else {
        this.selectedStatuses = this.statusItems.slice().map((t) => t.value)
      }
    },
    selectAllCategoriesAndReload() {
      this.$nextTick(async () => {
        this.selectAllCategories()
        await this.searchAction()
      })
    },
    selectAllTags() {
      this.$nextTick(() => {
        if (this.allTagsSelected) {
          this.selectedTags = []
        } else {
          this.selectedTags = this.propertyTags.slice().map((t) => t.value)
        }
        this.searchAction()
      })
    },
    iconSelected(all, some) {
      if (all) return 'mdi-close-box'
      if (some) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    selectAllStatusesAndReload() {
      this.$nextTick(async () => {
        this.selectAllStatuses()
        await this.searchAction()
      })
    },
    async openCreateMultiplePaymentsDialog() {
      const success = await this.$refs.createMultiplePayments.open()
      if (success) {
        // this.$root.$setLoading(true)
        // this.progressMsg = `Processing CSV File...`
        this.$router.replace('/preview-multiple-payments')
      }
    }
  },
  sockets: {
    multiplePaymentsProgress(data) {
      const dataObj = JSON.parse(data)
      if (!dataObj.done) {
        this.progressMsg = `Creating multiple payments from CSV... (${dataObj.index}/${dataObj.count})`
      } else {
        this.progressMsg = ``
        this.$root.$setLoading(false)
      }
    }
  }
}
</script>

<style scoped>
table thead tr th {
  white-space: nowrap !important;
}
.card-container {
  border-radius: 12px !important;
}
.v-input {
  border-radius: 10px;
}
table tbody tr td {
  /*border: none !important;
    border-bottom: none !important;*/
  border-color: #eeeeee !important;
}
p {
  margin-bottom: 0;
}
.text-overflow {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.row-item-expanded td p {
  font-weight: bold;
}
.row-item-expanded td {
  border: none !important;
}
.expand-item td p {
  font-weight: 300;
  font-size: 11px !important;
}
.expand-item td {
  border: none !important;
}
tr.bottom-border td {
  /*border-bottom: thin solid #CCC !important;*/
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}
.table-footer-prepend {
  margin-top: -54px;
  height: 48px;
}
.test {
  border: 2px solid yellow;
}
</style>
